/* Pictures in the lesson summary: drop zone, paste, picker, thumbnails, viewer */
import { t } from '../i18n/index.js';
import { PIC_MAX, deletePics, mediaOn, picURL, uploadPic } from '../cloud/media.js';
import { persist } from '../state.js';
import { IC, ask, esc, toast } from '../ui.js';

const isImg=f=>f&&/^image\//.test(f.type);

/* thumbnails into a container; returns nothing — fills <img> as URLs arrive */
export function fillThumbs(root){
  root.querySelectorAll('img[data-path]').forEach(async img=>{
    try{img.src=await picURL(img.dataset.path);img.closest('.pic')?.classList.remove('loading')}
    catch(e){img.closest('.pic')?.classList.add('broken')}
  });
}
export function thumbsHtml(ses,editable){
  return (ses.pics||[]).map((p,i)=>`<div class="pic loading"><button type="button" class="pic-open" data-pico="${i}" aria-label="${esc(t('pic.open'))}"><img data-path="${esc(p.path)}" alt="" width="${p.w||160}" height="${p.h||120}"></button>${editable?`<button type="button" class="pic-x" data-picx="${i}" aria-label="${esc(t('c.delete'))}">${IC.x}</button>`:''}</div>`).join('');
}

/* full-screen viewer: pinch or double-tap to zoom, drag to move a zoomed picture,
   swipe / arrows to go to the next one, Esc or tap outside to close. Mouse wheel zooms on a computer. */
export function viewPics(ses,start){
  const list=ses.pics||[];if(!list.length)return;
  let i=start||0;
  const o=document.createElement('div');o.className='picview';o.setAttribute('role','dialog');o.setAttribute('aria-modal','true');
  o.innerHTML=`<button class="pv-x" aria-label="${esc(t('c.close'))}">${IC.x}</button>${list.length>1?'<button class="pv-nav prev" aria-label="‹">‹</button><button class="pv-nav next" aria-label="›">›</button>':''}<img alt="" draggable="false"><div class="pv-n"></div>`;
  document.body.appendChild(o);
  const img=o.querySelector('img');
  let sc=1,tx=0,ty=0;
  const MAX=5;
  const apply=anim=>{img.classList.toggle('anim',!!anim);img.style.transform=`translate(${tx}px,${ty}px) scale(${sc})`;o.classList.toggle('zoomed',sc>1.01)};
  /* the picture's untransformed box (layout size, so it doesn't depend on the zoom being applied yet) */
  const base=()=>{const w=img.offsetWidth,h=img.offsetHeight;return{w,h,cx:img.offsetLeft+w/2,cy:img.offsetTop+h/2}};
  /* keep the picture covering the screen edges it can reach; snap back to 1 when nearly unzoomed */
  const clamp=anim=>{
    if(sc<=1.02){sc=1;tx=0;ty=0;apply(anim);return}
    const b=base(),lx=Math.max(0,(b.w*sc-innerWidth)/2+16),ly=Math.max(0,(b.h*sc-innerHeight)/2+16);
    tx=Math.min(lx,Math.max(-lx,tx));ty=Math.min(ly,Math.max(-ly,ty));apply(anim);
  };
  /* zoom to scale s keeping the screen point (px,py) where it is */
  const zoomAt=(s,px,py,anim)=>{const b=base();s=Math.min(MAX,Math.max(1,s));
    const qx=(px-b.cx-tx)/sc,qy=(py-b.cy-ty)/sc;tx=px-b.cx-qx*s;ty=py-b.cy-qy*s;sc=s;clamp(anim)};
  const reset=()=>{sc=1;tx=0;ty=0;apply(false)};
  const show=async()=>{reset();o.querySelector('.pv-n').textContent=list.length>1?(i+1)+' / '+list.length:'';img.removeAttribute('src');try{img.src=await picURL(list[i].path)}catch(e){}};
  const go=d=>{i=(i+d+list.length)%list.length;show()};
  const close=()=>{document.removeEventListener('keydown',key,true);o.remove()};
  const key=e=>{if(e.key==='Escape'){e.stopPropagation();close()}else if(e.key==='ArrowRight'&&sc===1)go(1);else if(e.key==='ArrowLeft'&&sc===1)go(-1);
    else if(e.key==='+'||e.key==='=')zoomAt(sc*1.5,innerWidth/2,innerHeight/2,true);else if(e.key==='-')zoomAt(sc/1.5,innerWidth/2,innerHeight/2,true)};
  document.addEventListener('keydown',key,true);
  let moved=false;
  o.onclick=e=>{if(moved){moved=false;return}if(e.target===o||e.target.closest('.pv-x'))close()};
  o.querySelector('.prev')?.addEventListener('click',e=>{e.stopPropagation();go(-1)});
  o.querySelector('.next')?.addEventListener('click',e=>{e.stopPropagation();go(1)});
  /* gestures */
  const pts=new Map();let pinch=null,pan=null,lastTap=0,lastTapX=0,lastTapY=0;
  const dist=(a,b)=>Math.hypot(a.x-b.x,a.y-b.y),mid=(a,b)=>({x:(a.x+b.x)/2,y:(a.y+b.y)/2});
  img.addEventListener('pointerdown',e=>{
    e.preventDefault();try{img.setPointerCapture(e.pointerId)}catch(x){}
    pts.set(e.pointerId,{x:e.clientX,y:e.clientY});
    if(pts.size===2){const[a,b]=[...pts.values()];pinch={d:dist(a,b),s:sc,m:mid(a,b)};pan=null}
    else if(pts.size===1)pan={x:e.clientX,y:e.clientY,tx,ty,t:Date.now()};
  });
  img.addEventListener('pointermove',e=>{
    if(!pts.has(e.pointerId))return;pts.set(e.pointerId,{x:e.clientX,y:e.clientY});
    if(pinch&&pts.size>=2){const[a,b]=[...pts.values()];const m=mid(a,b);
      zoomAt(pinch.s*dist(a,b)/pinch.d,pinch.m.x,pinch.m.y,false);tx+=m.x-pinch.m.x;ty+=m.y-pinch.m.y;pinch.m=m;pinch.s=sc;pinch.d=dist(a,b);apply(false);moved=true;return}
    if(pan){const dx=e.clientX-pan.x,dy=e.clientY-pan.y;if(Math.abs(dx)+Math.abs(dy)>6)moved=true;
      if(sc>1){tx=pan.tx+dx;ty=pan.ty+dy;apply(false)}
      else{img.style.transform=`translateX(${dx*0.6}px)`}}
  });
  const up=e=>{
    if(!pts.has(e.pointerId))return;pts.delete(e.pointerId);
    if(pinch){if(pts.size<2){pinch=null;clamp(true);const r=[...pts.values()][0];pan=r?{x:r.x,y:r.y,tx,ty,t:Date.now()}:null}return}
    if(!pan)return;
    const dx=e.clientX-pan.x,dy=e.clientY-pan.y,small=Math.abs(dx)<10&&Math.abs(dy)<10;pan=null;
    if(small){
      const now=Date.now();
      if(now-lastTap<320&&Math.hypot(e.clientX-lastTapX,e.clientY-lastTapY)<40){lastTap=0;   // double tap
        if(sc>1){sc=1;tx=0;ty=0;apply(true)}else zoomAt(2.5,e.clientX,e.clientY,true);moved=true;return}
      lastTap=now;lastTapX=e.clientX;lastTapY=e.clientY;return;
    }
    if(sc>1){clamp(true);return}
    if(list.length>1&&Math.abs(dx)>60&&Math.abs(dx)>Math.abs(dy))go(dx<0?1:-1);else apply(true);
  };
  img.addEventListener('pointerup',up);img.addEventListener('pointercancel',up);
  o.addEventListener('wheel',e=>{e.preventDefault();zoomAt(sc*Math.exp(-e.deltaY*(e.ctrlKey?0.01:0.0025)),e.clientX,e.clientY,false)},{passive:false});
  show();o.querySelector('.pv-x').focus();
}

/* the editor block inside the summary sheet */
export function picsField(){
  return `<div class="field"><label>${t('pic.title')} <span class="tiny">(${t('c.optional')})</span></label><div id="rc-pics"></div></div>`;
}
export function bindPicsField(sheetEl,ses){
  const box=sheetEl.querySelector('#rc-pics');
  if(!mediaOn()){box.innerHTML=`<p class="hint">${t('pic.needLogin')}</p>`;return}
  let busy=0;
  const draw=()=>{
    const n=(ses.pics||[]).length;
    box.innerHTML=`<div class="picgrid">${thumbsHtml(ses,true)}${Array.from({length:busy},()=>'<div class="pic up"><span class="spin"></span></div>').join('')}
      ${n+busy<PIC_MAX?`<label class="pic add" tabindex="0">${IC.plus}<span>${t('pic.add')}</span><input type="file" accept="image/*" multiple hidden></label>`:''}</div>
      <p class="tiny pichint">${t('pic.hint')}</p>`;
    fillThumbs(box);
    box.querySelectorAll('[data-pico]').forEach(b=>b.onclick=()=>viewPics(ses,+b.dataset.pico));
    box.querySelectorAll('[data-picx]').forEach(b=>b.onclick=()=>ask(t('pic.delConfirm'),async()=>{
      const p=(ses.pics||[])[+b.dataset.picx];if(!p)return;
      ses.pics=ses.pics.filter(x=>x!==p);persist();draw();
      try{await deletePics([p.path])}catch(e){console.warn('pics',e)}
    },true));
    const add=box.querySelector('.pic.add');
    if(add){add.querySelector('input').onchange=e=>{take([...e.target.files]);e.target.value=''};
      add.onkeydown=e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();add.querySelector('input').click()}}}
  };
  const take=async files=>{
    files=files.filter(isImg);if(!files.length)return;
    const room=PIC_MAX-(ses.pics||[]).length-busy;
    if(room<=0){toast(t('pic.max',{n:PIC_MAX}));return}
    if(files.length>room){toast(t('pic.max',{n:PIC_MAX}));files=files.slice(0,room)}
    busy+=files.length;draw();
    for(const f of files){
      try{const p=await uploadPic(ses,f);ses.pics=[...(ses.pics||[]),p];persist()}
      catch(e){console.error('pic upload',e);toast(t('pic.err'))}
      busy--;draw();
    }
  };
  /* drop anywhere on the sheet, paste anywhere in it */
  const zone=sheetEl;
  let depth=0;
  zone.addEventListener('dragenter',e=>{if([...(e.dataTransfer?.items||[])].some(x=>x.kind==='file')){depth++;zone.classList.add('dropping')}});
  zone.addEventListener('dragleave',()=>{if(--depth<=0){depth=0;zone.classList.remove('dropping')}});
  zone.addEventListener('dragover',e=>{if([...(e.dataTransfer?.items||[])].some(x=>x.kind==='file'))e.preventDefault()});
  zone.addEventListener('drop',e=>{const fs=[...(e.dataTransfer?.files||[])];if(!fs.length)return;e.preventDefault();depth=0;zone.classList.remove('dropping');take(fs)});
  zone.addEventListener('paste',e=>{const fs=[...(e.clipboardData?.files||[])].filter(isImg);if(!fs.length)return;e.preventDefault();take(fs)});
  draw();
}
